import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorageUsage as StorageUsageType } from '../../../types/storage';
import storageService from '../../../services/community/storageService';
import { formatBytes } from '../../../utils/formatUtils';

export default function StorageUsage(): JSX.Element {
  const { t } = useTranslation();
  const [storageUsage, setStorageUsage] = useState<StorageUsageType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const fetchStorageUsage = async () => {
      try {
        setLoading(true);
        const data = await storageService.getStorageUsage();
        setStorageUsage(data);
        setError(false);
      } catch (err) {
        console.error('Failed to fetch storage usage:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchStorageUsage();
  }, []);

  if (loading) {
    return (
      <div className="w-full rounded-lg border border-secondary-200 bg-secondary-50 p-4">
        <div className="h-4 w-1/3 animate-pulse rounded bg-secondary-200" />
        <div className="mt-2 h-6 w-full animate-pulse rounded bg-secondary-200" />
      </div>
    );
  }

  if (error || !storageUsage) {
    return (
      <div className="w-full rounded-lg border border-secondary-200 bg-secondary-50 p-4">
        <p className="text-sm text-primary-700">{t('page.settings.general.storage.error')}</p>
      </div>
    );
  }

  const usagePercentage = (storageUsage.total / storageUsage.maxUsage) * 100;
  const photoAlbumsPercentage = (storageUsage.photoAlbums / storageUsage.maxUsage) * 100;
  const fileStoragePercentage = (storageUsage.fileStorage / storageUsage.maxUsage) * 100;
  const isNearLimit = usagePercentage >= 80;
  const isAtLimit = usagePercentage >= 100;

  return (
    <div className="w-full rounded-lg border border-secondary-200 bg-secondary-50 p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-primary-900">
          {t('page.settings.general.storage.title')}
        </h3>
        <span className="text-xs text-primary-700">
          {formatBytes(storageUsage.total)} / {formatBytes(storageUsage.maxUsage)}
        </span>
      </div>

      {/* Progress bar with segments */}
      <div className="relative h-6 w-full overflow-hidden rounded-full bg-secondary-200">
        <div className="flex h-full">
          {/* Photo Albums segment */}
          <div
            className={`h-full transition-all duration-500 ${
              isAtLimit ? 'bg-red-600' : isNearLimit ? 'bg-yellow-500' : 'bg-primary-300'
            }`}
            style={{ width: `${Math.min(photoAlbumsPercentage, 100)}%` }}
          />
          {/* File Storage segment */}
          <div
            className={`h-full transition-all duration-500 ${
              isAtLimit ? 'bg-red-700' : isNearLimit ? 'bg-yellow-600' : 'bg-primary-900'
            }`}
            style={{ width: `${Math.min(fileStoragePercentage, 100 - photoAlbumsPercentage)}%` }}
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-medium text-primary-900">
            {usagePercentage.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Breakdown with legend */}
      <div className="mt-3 space-y-1 text-xs text-primary-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className={`h-3 w-3 rounded ${
                isAtLimit ? 'bg-red-600' : isNearLimit ? 'bg-yellow-500' : 'bg-primary-300'
              }`}
            />
            <span>{t('page.settings.general.storage.photoAlbums')}:</span>
          </div>
          <span className="font-medium">{formatBytes(storageUsage.photoAlbums)}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className={`h-3 w-3 rounded ${
                isAtLimit ? 'bg-red-700' : isNearLimit ? 'bg-yellow-600' : 'bg-primary-900'
              }`}
            />
            <span>{t('page.settings.general.storage.fileStorage')}:</span>
          </div>
          <span className="font-medium">{formatBytes(storageUsage.fileStorage)}</span>
        </div>
      </div>

      {isNearLimit && (
        <div className="mt-3 rounded-md bg-primary-300 p-2">
          <p className="text-xs text-primary-900">
            {isAtLimit
              ? t('page.settings.general.storage.atLimit')
              : t('page.settings.general.storage.nearLimit')}
          </p>
        </div>
      )}
    </div>
  );
}
